import { action, makeObservable, observable } from 'mobx';
import { DataResponse } from 'services/response';
import {
  IWasteGroupingEnvironmentPermitServices,
  WasteGroupingEnvironmentPermitModel,
  WasteGroupingEnvironmentPermitSearchPayload,
  WasteGroupingEnvironmentPermitExportPayload,
} from 'services/RawData/wasteGroupingEnvironmentPermit_services';

export type WasteGroupingEnvironmentPermitStore = {
  searchResponse: {
    data: WasteGroupingEnvironmentPermitModel[];
    isSuccess: boolean;
    message: string;
    paging?: any;
  };
  detailResponse: {
    data?: WasteGroupingEnvironmentPermitModel;
    isSuccess: boolean;
    message: string;
  };
  crudResponse: {
    isSuccess: boolean;
    message: string;
  };
  exportResponse: {
    isSuccess: boolean;
    message: string;
  };
  importResponse: {
    isSuccess: boolean;
    message: string;
  };
};

export interface IWasteGroupingEnvironmentPermitPresenter {
  searchWasteGroupingEnvironmentPermits(
    store: WasteGroupingEnvironmentPermitStore,
    payload: WasteGroupingEnvironmentPermitSearchPayload
  ): Promise<void>;

  getWasteGroupingEnvironmentPermitDetail(
    store: WasteGroupingEnvironmentPermitStore,
    id: string
  ): Promise<void>;

  createWasteGroupingEnvironmentPermit(
    store: WasteGroupingEnvironmentPermitStore,
    payload: WasteGroupingEnvironmentPermitModel
  ): Promise<void>;

  updateWasteGroupingEnvironmentPermit(
    store: WasteGroupingEnvironmentPermitStore,
    payload: WasteGroupingEnvironmentPermitModel
  ): Promise<void>;

  deleteWasteGroupingEnvironmentPermit(
    store: WasteGroupingEnvironmentPermitStore,
    id: string
  ): Promise<void>;

  exportTemplateCreate(
    store: WasteGroupingEnvironmentPermitStore,
    payload: WasteGroupingEnvironmentPermitExportPayload
  ): Promise<void>;

  importWasteGroupingEnvironmentPermit(
    store: WasteGroupingEnvironmentPermitStore,
    file: File
  ): Promise<void>;

  exportTemplateAdd(
    store: WasteGroupingEnvironmentPermitStore,
    payload?: WasteGroupingEnvironmentPermitExportPayload
  ): Promise<void>;

  importWasteGroupingEnvironmentPermitCreate(
    store: WasteGroupingEnvironmentPermitStore,
    file: File
  ): Promise<void>;
}

export const createWasteGroupingEnvironmentPermitPresenter = (
  services: IWasteGroupingEnvironmentPermitServices
): IWasteGroupingEnvironmentPermitPresenter => {
  return {
    searchWasteGroupingEnvironmentPermits: action(async (store, payload) => {
      try {
        const result = await services.searchWasteGroupingEnvironmentPermits(payload);
        store.searchResponse.data = result.data;
        store.searchResponse.isSuccess = result.isSuccess;
        store.searchResponse.message = result.message;
        store.searchResponse.paging = result.paging;
      } catch (error: any) {
        store.searchResponse.isSuccess = false;
        store.searchResponse.message = error?.message || 'Có lỗi xảy ra';
        store.searchResponse.data = [];
      }
    }),

    getWasteGroupingEnvironmentPermitDetail: action(async (store, id) => {
      try {
        const result = await services.getDetailWasteGroupingEnvironmentPermit(id);
        store.detailResponse.data = result.data;
        store.detailResponse.isSuccess = result.isSuccess;
        store.detailResponse.message = result.message;
      } catch (error: any) {
        store.detailResponse.isSuccess = false;
        store.detailResponse.message = error?.message || 'Có lỗi xảy ra';
        store.detailResponse.data = undefined;
      }
    }),

    createWasteGroupingEnvironmentPermit: action(async (store, payload) => {
      try {
        const result = await services.createWasteGroupingEnvironmentPermit(payload);
        store.crudResponse.isSuccess = result.isSuccess;
        store.crudResponse.message = result.message;
      } catch (error: any) {
        store.crudResponse.isSuccess = false;
        store.crudResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),

    updateWasteGroupingEnvironmentPermit: action(async (store, payload) => {
      try {
        const result = await services.updateWasteGroupingEnvironmentPermit(payload);
        store.crudResponse.isSuccess = result.isSuccess;
        store.crudResponse.message = result.message;
      } catch (error: any) {
        store.crudResponse.isSuccess = false;
        store.crudResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),

    deleteWasteGroupingEnvironmentPermit: action(async (store, id) => {
      try {
        const result = await services.deleteWasteGroupingEnvironmentPermit(id);
        store.crudResponse.isSuccess = result.isSuccess;
        store.crudResponse.message = result.message;
      } catch (error: any) {
        store.crudResponse.isSuccess = false;
        store.crudResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),

    exportTemplateCreate: action(async (store, payload) => {
      try {
        await services.exportTemplateCreate(payload);
        store.exportResponse.isSuccess = true;
        store.exportResponse.message = 'Export template thành công';
      } catch (error: any) {
        store.exportResponse.isSuccess = false;
        store.exportResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),

    importWasteGroupingEnvironmentPermit: action(async (store, file) => {
      try {
        const result = await services.importWasteGroupingEnvironmentPermit(file);
        store.importResponse.isSuccess = result.isSuccess;
        store.importResponse.message = result.message;
      } catch (error: any) {
        store.importResponse.isSuccess = false;
        store.importResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),

    exportTemplateAdd: action(async (store, payload) => {
      try {
        await services.exportTemplateAdd(payload);
        store.exportResponse.isSuccess = true;
        store.exportResponse.message = 'Export template thành công';
      } catch (error: any) {
        store.exportResponse.isSuccess = false;
        store.exportResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),

    importWasteGroupingEnvironmentPermitCreate: action(async (store, file) => {
      try {
        const result = await services.importWasteGroupingEnvironmentPermitCreate(file);
        store.importResponse.isSuccess = result.isSuccess;
        store.importResponse.message = result.message;
      } catch (error: any) {
        store.importResponse.isSuccess = false;
        store.importResponse.message = error?.message || 'Có lỗi xảy ra';
      }
    }),
  };
};

export const createWasteGroupingEnvironmentPermitStore = (): WasteGroupingEnvironmentPermitStore => {
  const store: WasteGroupingEnvironmentPermitStore = {
    searchResponse: {
      data: [],
      isSuccess: false,
      message: '',
      paging: undefined,
    },
    detailResponse: {
      data: undefined,
      isSuccess: false,
      message: '',
    },
    crudResponse: {
      isSuccess: false,
      message: '',
    },
    exportResponse: {
      isSuccess: false,
      message: '',
    },
    importResponse: {
      isSuccess: false,
      message: '',
    },
  };

  makeObservable(store, {
    searchResponse: observable,
    detailResponse: observable,
    crudResponse: observable,
    exportResponse: observable,
    importResponse: observable,
  });

  return store;
};

